import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, CloseButton, Form, Offcanvas } from "react-bootstrap";
import { FaPencilAlt, FaThumbtack, FaUndo } from "react-icons/fa";
import { CLAIM_PANEL_DEFAULTS, CLAIM_PANEL_MIN_WIDTH } from "../../../services/claimPanelPreferences.js";
import "../../styles/claim-glance.css";
import { updateClaim } from "../../services/apiClient.js";
import { claimMailtoHref, claimMailtoTitle } from "../../utils/claimEmail.js";
import Hint from "./../shared/Hint.jsx";

// "Job at a glance" — available from every tab, either in the overlay drawer
// or an optional pinned panel. Layout preferences belong to the signed-in user;
// the claim facts and their save path stay shared with Details.
//
// QUICK FACTS (RDR Note · Lockbox · Job Summary) are EDITABLE here — the 2.0
// answer to 1.0's Progress-page quick box. Their home is the Details tab; this
// is the same field through the one sidebar every tab already has, one save
// path (PUT /claims/:id) — access from anywhere, never a second home
// (no-duplicate-entry rule, Robert 2026-07-16).

// The assessor-routing contacts, in 1.0's header order.
const ROUTING_ROLES = [
  ["assessor_updates_to", "Updates To"],
  ["assessor_quotes_to", "Quotes To"],
  ["assessor_invoices_to", "Invoices To"],
  ["assessor_variations_to", "Variations To"],
];

const SHORT_LABELS = {
  "Assessor Co": "Assr co", "Assessor": "Assessor", "Assessor ph": "Assr ph",
  "Assessor ref": "Assr ref", "Claim number": "Claim no.",
  "Updates To": "Updates", "Quotes To": "Quotes", "Invoices To": "Invoices",
  "Variations To": "Variations", "Job type": "Type", "Job Summary": "Summary",
};

function FactLabel({ label, editable = false }) {
  return (
    <span className="claim-glance-label text-muted small" data-hint={label}>
      <span className="visually-hidden">{label}</span>
      <span aria-hidden="true" className="claim-glance-label-full">{label}</span>
      <span aria-hidden="true" className="claim-glance-label-short">{SHORT_LABELS[label] || label}</span>
      {editable ? <FaPencilAlt aria-hidden="true" className="ms-1 opacity-50" style={{ fontSize: "0.65rem" }} /> : null}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="claim-glance-row py-1">
      <FactLabel label={label} />
      <span className="claim-glance-value small">{value || "—"}</span>
    </div>
  );
}

// Email actions keep the existing subject/CC rules and expose the full address
// in the shared hint, even when a long recipient name wraps in the narrow pane.
function EmailRow({ label, text, email, cc, claim, client = false }) {
  const href = claimMailtoHref(email, claim, cc, { client });
  return <Row label={label} value={href
    ? <a href={href} data-hint={claimMailtoTitle(email, cc)}>{text || email}</a>
    : text} />;
}

// One editable quick fact: click the value (or pencil) → input → Enter/Save.
function QuickFact({ claim, field, label, multiline = false, value, onSaved, editor, onEditorChange }) {
  // Drafts live above both presentations so pinning/unpinning or resizing the
  // browser never discards an unfinished edit.
  const { editing = false, draft = "", busy = false, error = "" } = editor || {};
  const setEditing = (editing) => onEditorChange({ editing });
  const setDraft = (draft) => onEditorChange({ draft });
  const setBusy = (busy) => onEditorChange({ busy });
  const setError = (error) => onEditorChange({ error });

  function start() {
    setDraft(value || "");
    setError("");
    setEditing(true);
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      await updateClaim(claim.id, { [field]: draft.trim() || null });
      onSaved(field, draft.trim() || null);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="py-1">
        <Form.Label className="text-muted small mb-1" htmlFor={`glance-edit-${field}`}>{label}</Form.Label>
        <Form.Control
          id={`glance-edit-${field}`}
          size="sm"
          as={multiline ? "textarea" : "input"}
          rows={multiline ? 3 : undefined}
          value={draft}
          autoFocus
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !multiline) save();
            if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
          }}
        />
        {error ? <div className="small text-danger">{error}</div> : null}
        <div className="d-flex gap-1 mt-1">
          <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      role="button"
      className="claim-glance-row py-1 glance-quick-fact"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); start(); } }}
      data-hint={`Click to edit ${label.toLowerCase()}`}
      onClick={start}
    >
      <FactLabel label={label} editable />
      <span className="claim-glance-value small">
        {value || <span className="text-muted">—</span>}
      </span>
    </div>
  );
}

// One quick FLAG (Important / Problem / Contractor Quoting — 1.0's Progress-page
// checkboxes, homed on Details Flags): a switch that saves immediately. The
// server rules ride along free (un-ticking Problem Job is Administrator-only;
// ticking it stamps the date) — a refusal shows inline.
function QuickFlag({ claim, field, label, value, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggle(next) {
    setBusy(true);
    setError("");
    try {
      await updateClaim(claim.id, { [field]: next });
      onSaved(field, next);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="py-1">
      <Form.Check
        type="switch"
        id={`glance-${field}`}
        label={<span className={`small ${value ? "text-danger fw-semibold" : "text-muted"}`}>{label}</span>}
        checked={!!value}
        disabled={busy}
        onChange={(e) => toggle(e.target.checked)}
      />
      {error ? <div className="small text-danger">{error}</div> : null}
    </div>
  );
}

export function ClaimGlanceButton({ onClick, expanded, pinned }) {
  return (
    <Hint label={pinned ? "Job at a glance — pinned" : "Job at a glance"}>
      <Button variant="outline-secondary" size="sm"
        className="rounded-circle d-inline-flex align-items-center justify-content-center p-0"
        style={{ width: "1.55rem", height: "1.55rem", fontSize: "0.9rem", lineHeight: 1 }}
        aria-label="Job at a glance" aria-expanded={expanded}
        onClick={(e) => { e.currentTarget.blur(); onClick(); }}>
        i
      </Button>
    </Hint>
  );
}

function ClaimGlance({ claim, panel, onSaved }) {
  const [editors, setEditors] = useState({});
  const [dragWidth, setDragWidth] = useState(null);
  const drag = useRef(null);
  const dockRef = useRef(null);
  const docked = panel.preferences.pinned && panel.canDock;
  const width = Math.max(CLAIM_PANEL_MIN_WIDTH, Math.min(dragWidth ?? panel.width, panel.maxWidth));
  const edit = (field, patch) => setEditors((all) => ({ ...all, [field]: { ...all[field], ...patch } }));

  useEffect(() => {
    if (docked && panel.show) dockRef.current?.focus({ preventScroll: true });
  }, [docked, panel.show]);

  // Losing docking space must not save a smaller preference over the user's
  // desktop width. A drag is committed only on release; Escape cancels it.
  useEffect(() => {
    if (!docked) { drag.current = null; setDragWidth(null); }
  }, [docked]);

  const cancelDrag = () => { drag.current = null; setDragWidth(null); };
  const header = (
    <>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center column-gap-2 row-gap-1">
          <h6 className="mb-0" id="claim-glance-heading">Claim #{claim.claim_number}</h6>
          {!docked ? <Badge bg="secondary">{claim.status_name}</Badge> : null}
        </div>
      </div>
      <div className="d-flex align-items-center gap-1 flex-shrink-0">
        {(dragWidth ?? panel.preferences.width) !== CLAIM_PANEL_DEFAULTS.width ? (
          <Hint label="Reset panel width">
            <Button size="sm" variant="link" className="claim-glance-tool text-body-secondary" aria-label="Reset panel width"
              onClick={() => { cancelDrag(); panel.resize(CLAIM_PANEL_DEFAULTS.width); }}><FaUndo /></Button>
          </Hint>
        ) : null}
        <Hint label={panel.preferences.pinned ? "Unpin panel" : "Pin panel open"}>
          <Button size="sm" variant="link"
            className={`claim-glance-tool ${panel.preferences.pinned ? "text-primary" : "text-body-secondary"}`}
            aria-label={panel.preferences.pinned ? "Unpin panel" : "Pin panel open"}
            aria-pressed={panel.preferences.pinned}
            onClick={() => panel.pin(!panel.preferences.pinned)}><FaThumbtack /></Button>
        </Hint>
        <Hint label="Close job information">
          <CloseButton aria-label="Close job information" onClick={panel.close} />
        </Hint>
      </div>
    </>
  );
  const body = (
    <>
      {panel.error ? (
        <div className="alert alert-warning py-2 small" role="status">
          {panel.error} <Button size="sm" variant="link" className="p-0" onClick={panel.retry}>Retry</Button>
        </div>
      ) : null}
      {panel.preferences.pinned && !panel.canDock ? (
        <p className="small text-muted">Pinned for wider windows. Showing over the page here to keep the workspace usable.</p>
      ) : null}
          <Row label="Client" value={claim.client_name} />
          <Row label="Contact" value={claim.contact_name} />
          <Row label="Address" value={claim.street_address} />
          <Row label="Mobile" value={claim.mobile_phone} />
          <Row label="Phone" value={claim.business_phone} />
          <EmailRow label="Email" text={claim.email_address} email={claim.email_address} claim={claim} client />
          <Row label="Job type" value={claim.job_type_name} />
          <Row label="Division" value={claim.division_name} />
          <Row label="Insurer" value={claim.insurer_company_name} />
          <Row label="Assessor Co" value={claim.assessor_company_name} />
          <EmailRow label="Assessor" text={claim.assessor_name} email={claim.assessor_email}
            cc={claim.assessor_cc} claim={claim} />
          {/* The assessor's phone, where one is recorded (256 of 328 assessors
              have one) — KS 2026-09-08. A tel: link so a phone or a headset
              dials it; the row is absent rather than "—" when there is none. */}
          {claim.assessor_phone?.trim() ? (
            <Row label="Assessor ph" value={
              <a href={`tel:${claim.assessor_phone.replace(/[^\d+]/g, "")}`}>{claim.assessor_phone}</a>
            } />
          ) : null}
          {/* 1.0's header carried all four routing contacts as mailto links
              (ClientInfo.ascx: Updates To · Quote To · Invoice To · Variations
              To). Shown only when they differ from the assessor, who is already
              a row above — they default to the assessor on 97% of jobs. */}
          {ROUTING_ROLES.map(([field, label]) =>
            claim[`${field}_name`] && claim[`${field}_name`] !== claim.assessor_name ? (
              <EmailRow key={field} label={label} text={claim[`${field}_name`]}
                email={claim[`${field}_email`]} cc={claim[`${field}_cc`]} claim={claim} />
            ) : null
          )}
          {/* 1.0 ClientInfo mails the PM with the same address-based subject
              as the client, not the assessor/reference subject. */}
          <EmailRow label="PCO" text={claim.project_coordinator_name}
            email={claim.project_coordinator_email} claim={claim} client />
          <Row label="Inspector" value={claim.inspector_name} />
          <Row label="Assessor ref" value={claim.assessor_ref} />
          <Row label="Claim number" value={claim.insurance_claim_number} />
          <Row label="Excess" value={claim.property_excess} />

          {/* Editable from any tab — one save path, home stays Details. */}
          <div
            className="text-uppercase fw-semibold small text-muted mt-2 mb-1"
            style={{ letterSpacing: "0.06em" }}
          >
            Quick Facts
          </div>
          <QuickFact claim={claim} field="lockbox_location" label="Lockbox"
            value={claim.lockbox_location} onSaved={onSaved} editor={editors.lockbox_location}
            onEditorChange={(patch) => edit("lockbox_location", patch)} />
          <QuickFact claim={claim} field="rdr_note" label="RDR Note" multiline
            value={claim.rdr_note} onSaved={onSaved} editor={editors.rdr_note}
            onEditorChange={(patch) => edit("rdr_note", patch)} />
          <QuickFact claim={claim} field="job_summary" label="Job Summary" multiline
            value={claim.job_summary} onSaved={onSaved} editor={editors.job_summary}
            onEditorChange={(patch) => edit("job_summary", patch)} />
          {/* 1.0's Progress-page checkboxes — same fields as Details Flags. */}
          <QuickFlag claim={claim} field="is_important_job" label="Important Job"
            value={claim.is_important_job} onSaved={onSaved} />
          <QuickFlag claim={claim} field="is_problem_job" label="Problem Job"
            value={claim.is_problem_job} onSaved={onSaved} />
          <QuickFlag claim={claim} field="is_contractor_quoting" label="Contractor Quoting"
            value={claim.is_contractor_quoting} onSaved={onSaved} />

          {!docked ? <div className="text-muted small mt-2">
            Quick facts save from here; everything else edits on the Details tab.
          </div> : null}

    </>
  );

  if (!docked) return (
    <Offcanvas show={panel.show} onHide={panel.close} placement="start" scroll backdrop
      restoreFocus={false} aria-labelledby="claim-glance-heading" className="claim-glance-overlay d-print-none">
      <Offcanvas.Header className="gap-2">{header}</Offcanvas.Header>
      <Offcanvas.Body className="claim-glance-content">{body}</Offcanvas.Body>
    </Offcanvas>
  );

  return (
    <Card className="claim-glance-docked border-0 rounded-0 shadow-none bg-transparent d-print-none" style={{ width }}
      role="region" aria-labelledby="claim-glance-heading" tabIndex={-1} ref={dockRef}>
      <Card.Header className="d-flex align-items-start gap-2 bg-transparent border-0 rounded-0 p-0 mb-2">{header}</Card.Header>
      <Card.Body className="claim-glance-content p-0">{body}</Card.Body>
      <span id="claim-glance-resize-help" className="visually-hidden">
        Drag to resize. Left and right arrow keys adjust the width; Home and End set the limits. Double-click resets the width.
      </span>
      <div className="claim-glance-resize" role="separator" tabIndex={0}
        aria-label="Resize job information" aria-orientation="vertical"
        aria-valuemin={CLAIM_PANEL_MIN_WIDTH} aria-valuemax={panel.maxWidth} aria-valuenow={Math.round(width)}
        aria-valuetext={`${Math.round(width)} pixels`}
        aria-describedby="claim-glance-resize-help"
        data-hint="Drag to resize · double-click to reset"
        data-hint-trigger="hover"
        onDoubleClick={() => panel.resize(CLAIM_PANEL_DEFAULTS.width)}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.currentTarget.focus();
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { id: e.pointerId, x: e.clientX, width, next: width };
        }}
        onPointerMove={(e) => {
          if (!drag.current || drag.current.id !== e.pointerId) return;
          const next = Math.max(CLAIM_PANEL_MIN_WIDTH, Math.min(panel.maxWidth, drag.current.width + e.clientX - drag.current.x));
          drag.current.next = next;
          setDragWidth(next);
        }}
        onPointerUp={(e) => {
          if (!drag.current || drag.current.id !== e.pointerId) return;
          const next = drag.current.next;
          cancelDrag();
          panel.resize(next);
        }}
        onPointerCancel={cancelDrag} onLostPointerCapture={cancelDrag}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); cancelDrag(); return; }
          const step = e.shiftKey ? 32 : 16;
          const next = e.key === "ArrowLeft" ? width - step : e.key === "ArrowRight" ? width + step
            : e.key === "Home" ? CLAIM_PANEL_MIN_WIDTH : e.key === "End" ? panel.maxWidth : null;
          if (next === null) return;
          e.preventDefault();
          panel.resize(Math.max(CLAIM_PANEL_MIN_WIDTH, Math.min(panel.maxWidth, next)));
        }} />
    </Card>
  );
}

export default ClaimGlance;
